import { Company, CompanyEmployee, CookiesManager, getDomain, MarketCode } from '@homesusa/core';
import {
  AddAxiosHeaderParam,
  CompanyEmployeeRoleHeader,
  CompanyHeader,
  MarketHeader
} from './axios';

export interface AuthSessionCookie {
  userId: string;
  market: MarketCode;
  company?: Company;
  companyEmployee?: CompanyEmployee;
}

export interface CompaniesSessionCookie {
  companies: Company[];
}

const defaultMarket = MarketCode.Sabor;
const userCookieName = '.Husa.Session';
const userCompaniesCookieName = '.Husa.Companies';
const currentDomain = getDomain(window.location.hostname);

function setCookie<T>(cookieName: string, value: T): void {
  CookiesManager.setCookie(cookieName, value, currentDomain);
}

const setAuthCookie = (value: AuthSessionCookie): void => setCookie(userCookieName, value);
const setCompaniesCookie = (value: CompaniesSessionCookie): void =>
  setCookie(userCompaniesCookieName, value);

export class AuthCookiesManager {
  public static getAuthCookie(): AuthSessionCookie {
    const cookie = CookiesManager.getCookie<AuthSessionCookie>(userCookieName);
    if (!cookie) {
      AddAxiosHeaderParam(MarketHeader, defaultMarket);
      const defaultCookie = {
        userId: '',
        market: defaultMarket
      };
      setAuthCookie(defaultCookie);
      return defaultCookie;
    }

    AddAxiosHeaderParam(MarketHeader, cookie.market);
    AddAxiosHeaderParam(CompanyHeader, cookie.company?.id);
    return cookie;
  }

  public static getCompaniesCookie(): CompaniesSessionCookie | undefined {
    return CookiesManager.getCookie<CompaniesSessionCookie>(userCompaniesCookieName);
  }

  public static setUserId(userId: string): void {
    const cookie = AuthCookiesManager.getAuthCookie();
    if (userId != null && userId !== '' && cookie?.userId === userId) {
      return;
    }

    CookiesManager.deleteCookie(userCompaniesCookieName);
    setAuthCookie({ userId, market: defaultMarket });
  }

  public static removeCompany(): void {
    const { userId, market } = AuthCookiesManager.getAuthCookie();
    setAuthCookie({ userId, market });
    AddAxiosHeaderParam(CompanyHeader, undefined);
  }

  public static setCompany(company: Company): void {
    const cookie = AuthCookiesManager.getAuthCookie();
    if (cookie.company?.id === company.id) {
      return;
    }

    if (company.marketCode == null) {
      throw new Error('Company must have a market code');
    }

    const market = company.marketCode;
    setAuthCookie({ ...cookie, market, company, companyEmployee: undefined });
    AddAxiosHeaderParam(CompanyHeader, company.id);
    AddAxiosHeaderParam(MarketHeader, market);
  }

  public static setCompanyEmployee(companyEmployee?: CompanyEmployee): void {
    const cookie = AuthCookiesManager.getAuthCookie();
    setAuthCookie({
      ...cookie,
      companyEmployee: cookie.company ? companyEmployee : undefined
    });
    AddAxiosHeaderParam(CompanyEmployeeRoleHeader, companyEmployee?.roleName);
  }

  public static setUserCompanies(userCompanies: Company[]): void {
    const cookie = AuthCookiesManager.getAuthCookie();
    if (cookie.userId == null || cookie.userId === '') {
      return;
    }
    setCompaniesCookie({ companies: userCompanies });
  }

  public static setMarket(market: MarketCode): void {
    const cookie = AuthCookiesManager.getAuthCookie();
    if (market === cookie.market) {
      return;
    }

    if (cookie.company && cookie.company.marketCode !== market) {
      AuthCookiesManager.removeCompany();
    }

    setAuthCookie({ ...cookie, market });
    AddAxiosHeaderParam(MarketHeader, market);
  }

  public static deleteCookie(): void {
    CookiesManager.deleteCookie(userCookieName);
    CookiesManager.deleteCookie(userCompaniesCookieName);
  }
}
